import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import pageUrls from 'constants/pagesUrls';

import type {
  YaClickEventType,
  YaSendFormEventType,
} from '../constants/yaEvents';
import pushAnalyticsEvent from '../utils/pushAnalyticsEvent';

export type ClickEvent = Pick<YaClickEventType, 'sourceComponent' | 'name'> & {
  event?: string;
};

export type SendFormEvent = Pick<
  YaSendFormEventType,
  'result' | 'sourceComponent' | 'name'
> & {
  event?: string;
};

export interface ScrollEvent {
  percent: number;
}
export type ClickAnalyticsFunctionType = (event: ClickEvent) => void;
export type SendFormAnalyticsFunctionType = (event: SendFormEvent) => void;
export type ScrollAnalyticsFunctionType = (event: ScrollEvent) => void;
export type ViewAnalyticsFunctionType = () => void;

export default function useAnalytics(): {
  clickAnalytics: ClickAnalyticsFunctionType;
  sendFormAnalytics: SendFormAnalyticsFunctionType;
  scrollAnalytics: ScrollAnalyticsFunctionType;
  viewAnalytics: ViewAnalyticsFunctionType;
} {
  const router = useRouter();
  const [pageName, setPageName] = useState('home');

  useEffect(() => {
    const page = Object.entries(pageUrls).find(entity => {
      return router.asPath === entity[1];
    });

    if (page) {
      setPageName(page[0]);
    }
  }, [router.asPath]);

  function clickAnalytics(event: ClickEvent): void {
    pushAnalyticsEvent({
      event: event.event ?? 'a3_click',
      sourcePage: pageName,
      sourceComponent: event.sourceComponent,
      name: event.name,
    });
  }

  function sendFormAnalytics(event: SendFormEvent): void {
    pushAnalyticsEvent({
      event: event.event ?? 'form_submitted',
      sourceComponent: event.sourceComponent,
      sourcePage: pageName,
      name: event.name,
      result: event.result,
    });
  }

  function scrollAnalytics(event: ScrollEvent): void {
    pushAnalyticsEvent({
      event: 'a3_scroll',
      sourcePage: pageName,
      percent: event.percent.toString(),
    });
  }

  function viewAnalytics(): void {
    pushAnalyticsEvent({
      event: 'landing_viewed',
      sourcePage: pageName,
    });
  }

  return { clickAnalytics, sendFormAnalytics, scrollAnalytics, viewAnalytics };
}
